import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/da1b5edrlbpoffc5h3bg";

function Contact() {
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const formEl = e.currentTarget;

    const fd = new FormData(formEl);
    const websiteAlt = (fd.get("website_alt") ?? "").toString().trim();
    if (websiteAlt) {
      setFormSuccess(true);
      formEl.reset();
      return;
    }

    setFormError("");
    setSubmitting(true);
    try {
      const payload = new URLSearchParams();
      fd.forEach((value, key) => {
        if (key === "website_alt" || value === "") return;
        payload.append(key, value.toString());
      });

      const response = await fetch(FORM_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
        },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } =
        {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }
      const serverMsg =
        parsed?.meta?.message ?? parsed?.meta?.detail ?? responseText ?? "";

      if (response.ok && parsed?.code === "OK") {
        setFormSuccess(true);
        formEl.reset();
      } else if (
        serverMsg.includes("spam") ||
        serverMsg.includes("form data is spam")
      ) {
        setFormError("提交未成功，请稍后重试。");
      } else {
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setFormError("网络异常，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="w-full bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 items-start">
          <div>
            <span className="text-xs font-semibold tracking-widest text-primary-600 uppercase">
              Contact
            </span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl text-foreground-950">
              有好番想推荐？欢迎留言
            </h2>
            <p className="mt-3 max-w-md text-sm md:text-base leading-relaxed text-foreground-700">
              你有心头好的治愈番想安利给我们，或者对片单有什么建议？把作品名和理由留下，
              编辑部会认真看，说不定下一期片单里就有它。
            </p>

            <div className="mt-6 space-y-3">
              {[
                { icon: "ri-mail-line", label: "邮箱", value: "hello@readdy.ai" },
                { icon: "ri-time-line", label: "更新频率", value: "片单每周更新" },
                {
                  icon: "ri-chat-smile-line",
                  label: "互动",
                  value: "留言反馈会认真回复",
                },
              ].map((c) => (
                <div
                  key={c.label}
                  className="flex items-center gap-3 rounded-lg bg-background-100 border border-background-200/70 px-4 py-3"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className={`${c.icon} text-lg`}></i>
                  </span>
                  <div>
                    <div className="text-xs text-foreground-600">{c.label}</div>
                    <div className="text-sm font-medium text-foreground-950">
                      {c.value}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form="小狗看番 留言反馈表单"
            className="rounded-lg bg-background-100 border border-background-200/70 p-5 md:p-6"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                  你的昵称
                </label>
                <input
                  type="text"
                  name="name"
                  required
                  maxLength={30}
                  placeholder="怎么称呼你"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                  你的邮箱
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  maxLength={60}
                  placeholder="方便我们回复你"
                  className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="mb-1.5 block text-sm font-medium text-foreground-800">
                你想推荐的番（或建议）
              </label>
              <textarea
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="作品名 + 为什么值得推荐，或你想看到的片单方向……"
                className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-300 focus:ring-2 focus:ring-primary-100"
              ></textarea>
              <p className="mt-1 text-right text-xs text-foreground-500">
                最多 500 字
              </p>
            </div>

            <div className="spam-trap-field" aria-hidden="true">
              <label htmlFor="website_alt">网站</label>
              <input
                type="text"
                id="website_alt"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            {formError && (
              <div className="mt-4 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
                {formError}
              </div>
            )}
            {formSuccess && (
              <div className="mt-4 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-900">
                已收到你的推荐，感谢支持！我们会认真阅读。
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 disabled:opacity-60 transition-colors"
            >
              {submitting ? "提交中…" : "提交推荐"}
              <i className="ri-send-plane-line"></i>
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default Contact;